import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import { Toaster } from "@/components/ui/sonner";
import { api, setPresenterMask } from "@/lib/api";
import { setCurrency, setPresenterMode } from "@/lib/ui";
import AuthCallback from "@/components/AuthCallback";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { InactivityGuard } from "@/components/InactivityGuard";
import Login from "@/pages/Login";
import AdminPortal from "@/pages/AdminPortal";
import TeacherPortal from "@/pages/TeacherPortal";
import ParentPortal from "@/pages/ParentPortal";
import StudentPortal from "@/pages/StudentPortal";
import Admission from "@/pages/Admission";
import EntrancePortal from "@/pages/EntrancePortal";
import ResetPassword from "@/pages/ResetPassword";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-10 w-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function RolePortal() {
  const { user, loading } = useAuth();
  useEffect(() => {
    api.get("/settings").then((s) => s?.currency && setCurrency(s.currency)).catch(() => {});
  }, []);
  if (loading || user === null) return <Loading />;
  if (!user) return <Navigate to="/" replace />;
  const isPresenter = user.role === "presenter";
  setPresenterMask(isPresenter);
  setPresenterMode(isPresenter);
  if (user.role === "student") return <ErrorBoundary><StudentPortal /><InactivityGuard /></ErrorBoundary>;
  const canAdmin = ["admin", "staff", "presenter"].includes(user.role) || !!user.admin_access
    || Object.values(user.permissions || {}).some((v) => v && v !== "none");
  const canTeacher = user.role === "teacher" || !!user.also_teacher;
  const canParent = user.role === "parent" || !!user.also_parent;
  if (canParent && !canAdmin && !canTeacher) { try { sessionStorage.setItem("active_portal", "parent"); } catch (e) { /* noop */ } return <ErrorBoundary><ParentPortal /><InactivityGuard /></ErrorBoundary>; }
  return (
    <ErrorBoundary compact>
      <MultiRolePortal canAdmin={canAdmin} canTeacher={canTeacher} canParent={canParent} />
      <InactivityGuard />
    </ErrorBoundary>
  );
}

function NoAccess() {
  const { logout } = useAuth();
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-8 text-center" data-testid="no-access-screen">
      <h2 className="font-heading font-extrabold text-2xl text-foreground">Aucun accès attribué</h2>
      <p className="text-muted-foreground mt-2 max-w-md">Votre compte n'a pas encore de rôle ou de permission associée. Veuillez contacter l'administration de l'école.</p>
      <button onClick={logout}
        className="mt-6 rounded-md bg-primary text-primary-foreground px-5 py-2 text-sm font-medium"
        data-testid="no-access-logout-btn">Se déconnecter</button>
    </div>
  );
}

function MultiRolePortal({ canAdmin, canTeacher, canParent }) {
  const views = [];
  if (canAdmin) views.push({ key: "admin", label: "→ Portail Admin", Comp: AdminPortal });
  if (canTeacher) views.push({ key: "teacher", label: "→ Portail Enseignant", Comp: TeacherPortal });
  if (canParent) views.push({ key: "parent", label: "→ Portail Parent", Comp: ParentPortal });
  const [idx, setIdx] = useState(0);
  const safeIdx = idx < views.length ? idx : 0;
  useEffect(() => {
    const key = views[safeIdx]?.key;
    if (key) { try { sessionStorage.setItem("active_portal", key); } catch (e) { /* noop */ } }
  }, [safeIdx, views]);
  if (views.length === 0) return <NoAccess />;
  const Comp = views[safeIdx].Comp;
  const next = views[(safeIdx + 1) % views.length];
  return (
    <>
      {views.length > 1 && (
        <button
          onClick={() => setIdx((safeIdx + 1) % views.length)}
          data-testid="portal-switch-btn"
          className="fixed bottom-24 right-5 z-[80] rounded-full bg-primary text-primary-foreground shadow-lg px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
        >
          {next.label}
        </button>
      )}
      <Comp />
    </>
  );
}

function Root() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loading />;
  if (user) return <Navigate to="/app" replace />;
  return <Login />;
}

function PreviewBanner() {
  const { user } = useAuth();
  if (!user || !user._impersonated_by) return null;
  const exit = () => { try { sessionStorage.removeItem("impersonate_id"); } catch (e) { /* noop */ } window.location.assign("/app"); };
  return (
    <div className="fixed top-0 inset-x-0 z-[100] bg-amber-500 text-black text-sm px-4 py-1.5 flex items-center justify-center gap-3 shadow" data-testid="preview-banner">
      <span>👁 Mode Aperçu — {user.name} ({user.role})</span>
      <button onClick={exit} className="underline font-semibold" data-testid="exit-preview-btn">Quitter l'aperçu / Exit</button>
    </div>
  );
}

function AppInner() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <PreviewBanner />
      <Routes>
        <Route path="/" element={<Root />} />
        <Route path="/admission" element={<Admission />} />
        <Route path="/entrance" element={<EntrancePortal />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/app" element={<RolePortal />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <LangProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppInner />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </LangProvider>
    </div>
  );
}
